import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  MenuItem,
  IconButton,
  Chip,
  Card,
  CardContent,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Videocam as VideocamIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';

import VideoPlayer from './VideoPlayer';
import api from '../../services/nvr/api';

const NVRPlayback = () => {
  const [nvrConfig, setNvrConfig] = useState({
    ip: '192.168.219.102',
    port: 80,
    username: 'admin',
    password: 'admin1234',
  });

  const [playbackConfig, setPlaybackConfig] = useState({
    channel: 5,
    startTime: dayjs().format('YYYY-MM-DDTHH:mm'),
  });

  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [nvrConnections, setNvrConnections] = useState([]);
  const [selectedNvrId, setSelectedNvrId] = useState(null);

  // NVR 연결 목록 불러오기
  const loadNvrConnections = async () => {
    try {
      const response = await api.getNvrConnections();
      setNvrConnections(response.data.results || []);
    } catch (error) {
      console.error('NVR 목록 로드 실패:', error);
    }
  };

  useEffect(() => {
    loadNvrConnections();
  }, []);

  // 재생 시작
  const handleStartPlayback = async () => {
    setLoading(true);
    try {
      const playbackData = {
        channel: playbackConfig.channel,
        start_time: new Date(playbackConfig.startTime).toISOString(),
      };

      if (selectedNvrId) {
        playbackData.nvr_id = selectedNvrId;
      } else {
        Object.assign(playbackData, nvrConfig);
      }

      const response = await api.createPlaybackSession(playbackData);
      const session = response.data;

      setSessions([session, ...sessions]);
      setActiveSession(session);
      toast.success(`채널 ${playbackConfig.channel} 재생이 시작되었습니다.`);
    } catch (error) {
      console.error('재생 시작 실패:', error);
      toast.error('재생 시작에 실패했습니다: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  // 재생 중지
  const handleStopPlayback = async (sessionId) => {
    try {
      await api.stopPlaybackSession(sessionId);
      setSessions(sessions.filter(s => s.id !== sessionId));
      if (activeSession?.id === sessionId) {
        setActiveSession(null);
      }
      toast.info('재생이 중지되었습니다.');
    } catch (error) {
      console.error('재생 중지 실패:', error);
      toast.error('재생 중지에 실패했습니다.');
    }
  };

  // NVR 연결 테스트
  const handleTestConnection = async () => {
    setLoading(true);
    try {
      const nvr = await api.createNvrConnection(nvrConfig);
      await api.testNvrConnection(nvr.data.id);
      toast.success('NVR 연결 성공!');
      loadNvrConnections();
    } catch (error) {
      toast.error('NVR 연결 실패: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
        <VideocamIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        NVR 영상 재생 시스템
      </Typography>

      <Grid container spacing={3}>
        {/* 설정 패널 */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              <SettingsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              NVR 설정
            </Typography>

            {/* 저장된 NVR 선택 */}
            <TextField
              select
              fullWidth
              label="저장된 NVR"
              value={selectedNvrId || ''}
              onChange={(e) => setSelectedNvrId(e.target.value || null)}
              margin="normal"
            >
              <MenuItem value="">
                <em>새로운 연결</em>
              </MenuItem>
              {nvrConnections.map((nvr) => (
                <MenuItem key={nvr.id} value={nvr.id}>
                  {nvr.name || `${nvr.ip}:${nvr.port}`}
                </MenuItem>
              ))}
            </TextField>

            {/* 새로운 연결 설정 */}
            {!selectedNvrId && (
              <>
                <TextField
                  fullWidth
                  label="IP 주소"
                  value={nvrConfig.ip}
                  onChange={(e) => setNvrConfig({ ...nvrConfig, ip: e.target.value })}
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="포트"
                  type="number"
                  value={nvrConfig.port}
                  onChange={(e) => setNvrConfig({ ...nvrConfig, port: parseInt(e.target.value) })}
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="사용자명"
                  value={nvrConfig.username}
                  onChange={(e) => setNvrConfig({ ...nvrConfig, username: e.target.value })}
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="비밀번호"
                  type="password"
                  value={nvrConfig.password}
                  onChange={(e) => setNvrConfig({ ...nvrConfig, password: e.target.value })}
                  margin="normal"
                />
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleTestConnection}
                  disabled={loading}
                  sx={{ mt: 2 }}
                >
                  연결 테스트
                </Button>
              </>
            )}

            <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
              재생 설정
            </Typography>

            <TextField
              select
              fullWidth
              label="채널"
              value={playbackConfig.channel}
              onChange={(e) => setPlaybackConfig({ ...playbackConfig, channel: parseInt(e.target.value) })}
              margin="normal"
            >
              {[...Array(17)].map((_, i) => (
                <MenuItem key={i + 1} value={i + 1}>
                  채널 {i + 1}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              label="시작 시간"
              type="datetime-local"
              value={playbackConfig.startTime}
              onChange={(e) => setPlaybackConfig({ ...playbackConfig, startTime: e.target.value })}
              margin="normal"
              InputLabelProps={{
                shrink: true,
              }}
            />

            <Button
              fullWidth
              variant="contained"
              color="primary"
              size="large"
              startIcon={loading ? <CircularProgress size={20} /> : <PlayIcon />}
              onClick={handleStartPlayback}
              disabled={loading || (!selectedNvrId && !nvrConfig.ip)}
              sx={{ mt: 3 }}
            >
              재생 시작
            </Button>
          </Paper>

          {/* 활성 세션 목록 */}
          {sessions.length > 0 && (
            <Paper sx={{ p: 2, mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                활성 세션
              </Typography>
              {sessions.map((session) => (
                <Card key={session.id} sx={{ mb: 1 }}>
                  <CardContent sx={{ py: 1 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="body2">
                          채널 {session.channel}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {dayjs(session.start_time).format('YYYY-MM-DD HH:mm')}
                        </Typography>
                      </Box>
                      <Box>
                        {session.is_active && (
                          <Chip label="재생중" color="success" size="small" sx={{ mr: 1 }} />
                        )}
                        <IconButton
                          size="small"
                          onClick={() => handleStopPlayback(session.id)}
                          color="error"
                        >
                          <StopIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Paper>
          )}
        </Grid>

        {/* 비디오 플레이어 */}
        <Grid item xs={12} md={8}>
          {activeSession ? (
            <VideoPlayer
              session={activeSession}
              onClose={() => handleStopPlayback(activeSession.id)}
            />
          ) : (
            <Paper
              sx={{
                height: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#000',
              }}
            >
              <Box textAlign="center">
                <VideocamIcon sx={{ fontSize: 80, color: 'grey.600', mb: 2 }} />
                <Typography variant="h6" color="grey.600">
                  재생할 영상을 선택하세요
                </Typography>
                <Typography variant="body2" color="grey.700" sx={{ mt: 1 }}>
                  왼쪽 패널에서 NVR 설정 후 재생 버튼을 클릭하세요
                </Typography>
              </Box>
            </Paper>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default NVRPlayback; 